import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { fetchCaseBySlug } from '@api/cases'
import { ErrorPage } from '@components/ui/ErrorPage'
import { CasePage } from './CasePage'
import type { CasePageProps } from './CasePage'
import { useLang } from '@shared/i18n/useLang'
import { ssrCache } from '@shared/lib/ssrCache'

export function CasePageLoader() {
  const { slug } = useParams<{ slug: string }>()
  const lang = useLang()
  const cacheKey = slug ? `case:${lang}:${slug}` : null
  const seeded = cacheKey != null && ssrCache.has(cacheKey)

  const [data, setData] = useState<CasePageProps | null | 'loading' | 'error'>(
    () => (seeded ? (ssrCache.get<CasePageProps | null>(cacheKey!) ?? null) : 'loading'),
  )
  const [errorMsg, setErrorMsg] = useState<string>('')
  const skipNextFetch = useRef(seeded)

  useEffect(() => {
    if (!slug) return
    if (skipNextFetch.current) { skipNextFetch.current = false; return }
    setData('loading')
    fetchCaseBySlug(slug)
      .then(res => setData(res ?? null))
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err)
        console.error('[CasePageLoader] fetchCaseBySlug failed:', err)
        setErrorMsg(msg)
        setData('error')
      })
  }, [slug])

  if (data === 'loading') {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{
          width: 40, height: 40, borderRadius: '50%',
          border: '3px solid #D9DEE2',
          borderTopColor: '#189BD5',
          animation: 'spin 0.8s linear infinite',
        }} />
      </div>
    )
  }

  if (data === 'error') return (
    <>
      <ErrorPage code="500" />
      {import.meta.env.DEV && errorMsg && (
        <div style={{ position: 'fixed', bottom: 16, left: 16, right: 16, background: '#1e1e1e', color: '#ff6b6b', fontFamily: 'monospace', fontSize: 12, padding: '12px 16px', borderRadius: 4, zIndex: 9999, wordBreak: 'break-all', maxHeight: 120, overflow: 'auto' }}>
          <strong>DEV error:</strong> {errorMsg}
        </div>
      )}
    </>
  )
  if (!data) return <ErrorPage code="404" />

  return <CasePage {...data} />
}
